import React from "react";
import styled from "styled-components";
import { ProductTag } from "pages/public/LandingPage/styles";

const Section = styled.section`
  padding: 80px 400px;
  background: #fff;
  display: flex;
  flex-direction: column;

  /* 대형 태블릿 */
  @media (max-width: 1400px) {
    padding: 80px 200px;
  }

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    padding: 60px 100px;
  }

  /* 소형 태블릿 */
  @media (max-width: 768px) {
    padding: 40px 50px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    padding: 30px 20px;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  text-align: left;
  margin-bottom: 100px;
  max-width: 1024px;
  width: 100%;

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    margin-bottom: 80px;
    max-width: 100%;
  }

  /* 소형 태블릿 */
  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
    margin-bottom: 60px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    margin-bottom: 40px;
  }
`;

const SectionTitle = styled.div`
  font-size: 40px;
  font-weight: 700;
  color: #000;

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    font-size: 36px;
  }

  /* 소형 태블릿 */
  @media (max-width: 768px) {
    font-size: 32px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: #333;
  margin-bottom: 40px;

  /* 소형 태블릿 */
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 30px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 24px;
  }
`;

const SectionText = styled.p`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-size: 18px;
  color: #444;
  line-height: 180%;
  margin: 0;

  .lead {
    font-size: 22px;
    font-weight: 500;
    color: #111;
  }

  strong {
    font-weight: 600;
    color: #5c6eff;
  }

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    font-size: 17px;
    
    .lead {
      font-size: 20px;
    }
  }

  /* 소형 태블릿 */
  @media (max-width: 768px) {
    text-align: center;
    font-size: 16px;
    gap: 12px;
    
    .lead {
      font-size: 18px;
    }
  }

  /* 모바일 */
  @media (max-width: 480px) {
    font-size: 15px;
    gap: 10px;
    
    .lead {
      font-size: 17px;
    }
  }
`;

const CardGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  flex-wrap: wrap;

  /* 카드 3개가 들어가기 어려운 크기 */
  @media (max-width: 1200px) {
    gap: 24px;
  }

  /* 2개씩 배치 */
  @media (max-width: 1000px) {
    gap: 20px;
  }

  /* 세로 배치로 전환 */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const Card = styled.div`
  min-width: 330px;
  max-width: 100%;
  padding: 32px 24px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.25s ease;
  cursor: default;
  flex: 1;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(59, 92, 255, 0.12);
  }

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    padding: 28px 20px;
  }

  /* 2개씩 배치될 때 */
  @media (max-width: 1000px) {
    min-width: 280px;
  }

  /* 세로 배치 */
  @media (max-width: 768px) {
    width: 100%;
    max-width: 400px;
    flex: none;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    padding: 24px 20px;
    max-width: 100%;
  }
`;

const CardTitle = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: #5c6eff;
  margin-bottom: 18px;

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    font-size: 20px;
    margin-bottom: 16px;
  }

  /* 소형 태블릿 */
  @media (max-width: 768px) {
    font-size: 19px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 14px;
  }
`;

const CardDesc = styled.div`
  font-size: 16px;
  color: #555;
  line-height: 1.6;

  strong {
    display: block;
    font-weight: 600;
    margin-bottom: 16px;
  }

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    font-size: 15px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 1.7;
    
    strong {
      margin-bottom: 12px;
    }
  }
`;

const ProductSection: React.FC = () => {

  return (
    <Section>
      <TextContainer>
        <ProductTag>Product</ProductTag>
        <SectionTitle>Beyond Speech-to-Text</SectionTitle>
        <SectionSubtitle>음성의 모든 의미를 해석하는 AI 솔루션</SectionSubtitle>

        <SectionText>
          <span className="lead">
            음성 텍스트 인식은 기본, <strong>화자 분리</strong>·<strong>요약</strong>·<strong>키워드 추출</strong>까지 한 번에.
          </span>
          <span>
            감정·속도·높낮이 같은 <strong>바이오마커</strong> 분석으로 더 깊은 인사이트를 제공합니다.
          </span>
          <span>
            오디온 한 곳에서 <strong>실시간 인식</strong>부터 <strong>고도화된 분석</strong>까지,{" "}
            <strong>비즈니스 가치를 극대화</strong>하세요.
          </span>
        </SectionText>
      </TextContainer>

      <CardGrid>
  <Card>
    <CardTitle>① 음성 이해</CardTitle>
    <CardDesc>
      <strong>
      - 텍스트로의 정확한 변환<br />
      - 화자 분리<br />
      - 대화 요약 및 감정 분석<br />
      </strong>
      수집된 대화의 핵심 맥락과 분위기를<br /> 한눈에 파악할 수 있도록 도와드립니다.
    </CardDesc>
  </Card>

  <Card>
    <CardTitle>② 실시간 음성인식</CardTitle>
    <CardDesc>
      <strong>
      - 안정적인 한국어 인식<br />
      - 아이부터 시니어까지 맞춤형 인식<br />
      - 발화 속도나 패턴에 맞춰 커스텀 설정<br />
      </strong>
      비즈니스별 맞춤형 설정으로<br /> 더 자연스러운 인식 결과를 제공합니다.
    </CardDesc>
  </Card>

  <Card>
    <CardTitle>③ 보이스 하이라이트</CardTitle>
    <CardDesc>
    <strong style={{ marginBottom: "5px" }}>
    눈으로 듣는 새로운 방식  <br />
    </strong>
    <strong>
- 음성 속 속도와 강약, 높낮이<br />
- 침묵 구간 확인<br />
- 감정 분석<br />
      </strong>
보이스 하이라이트가 선명하게 보여드립니다.
    </CardDesc>
  </Card>
</CardGrid>
    </Section>
  );
};

export default ProductSection;
